import { createRouter, createWebHistory } from "vue-router";
import { useGlobalStore } from "./stores/global";
import { useAssessmentStore } from "./stores/assessment";
import analytics from "./services/analytics";

const DISABLE_ANALYTICS_KEY = "n0tr@cking";

const routes = [
  {
    path: "/:clinicId/assessment/",
    name: "home",
    props: true,
    component: () => import("./views/Home.vue"),
  },
  {
    path: "/:clinicId/assessment/:assessmentId",
    component: () => import("./views/Assessment.vue"),
    props: true,
    children: [
      {
        path: "",
        name: "assessment-initial-personal-info-gate",
        component: () =>
          import("./views/AssessmentInitialPersonalInfoGate.vue"),
        props: true,
      },
      {
        path: "questionnaire",
        name: "assessment-questionnaire",
        component: () => import("./views/AssessmentQuestionnaire.vue"),
        props: true,
      },
      {
        path: "audio-test",
        name: "assessment-audio-test",
        component: () => import("./views/AssessmentAudioTest.vue"),
        props: true,
      },
      {
        path: "personal-info",
        name: "assessment-personal-info",
        component: () => import("./views/AssessmentPersonalInfo.vue"),
        props: true,
      },
      {
        path: "result",
        name: "assessment-result",
        component: () => import("./views/AssessmentResult.vue"),
        props: true,
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not_found",
    redirect: (to) => {
      console.log("redirect", to);
      // If the path starts with a clinicId pattern, redirect to home
      const pathParts = to.path.split("/").filter((part) => part !== "");
      if (pathParts.length >= 1) {
        const clinicId = pathParts[0];
        return `/${clinicId}/assessment/`;
      }
      // Default fallback
      return "/clinic-1/assessment/";
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
});

router.beforeEach((to, from, next) => {
  console.log("router.beforeRouteEnter", { to, from });

  // Set clinicId in assessment store if route has clinicId parameter if it's not already set
  if (to.params.clinicId) {
    const assessmentStore = useAssessmentStore();

    if (
      assessmentStore.clinicId === undefined ||
      assessmentStore.clinicId === null
    ) {
      assessmentStore.clinicId = to.params.clinicId;
    }
  }

  if (to.query.testing) {
    const globalStore = useGlobalStore();

    globalStore.enableTestingMode();

    if (to.query.prefill) {
      globalStore.enablePrefillMode();
    }

    if (to.query.skipToWorkflowStep) {
      globalStore.setSkipToAssessmentWorkflowStep(to.query.skipToWorkflowStep);
    }
  }

  if (to.query["disable-analytics"] === DISABLE_ANALYTICS_KEY) {
    analytics.disable();
  }

  let queryUpdated = false;

  if (from.query.hasOwnProperty("a") && !to.query.hasOwnProperty("a")) {
    to.query.a = from.query.a;
    queryUpdated = true;
  }

  if (
    from.query["disable-analytics"] === DISABLE_ANALYTICS_KEY &&
    to.query["disable-analytics"] !== DISABLE_ANALYTICS_KEY
  ) {
    to.query["disable-analytics"] = from.query["disable-analytics"];
    queryUpdated = true;
  }

  if (
    from.query.hasOwnProperty("testing") &&
    !to.query.hasOwnProperty("testing")
  ) {
    to.query.testing = from.query.testing;
    queryUpdated = true;
  }

  if (
    from.query.hasOwnProperty("prefill") &&
    !to.query.hasOwnProperty("prefill")
  ) {
    to.query.prefill = from.query.prefill;
    queryUpdated = true;
  }

  if (
    from.query.hasOwnProperty("skipToWorkflowStep") &&
    !to.query.hasOwnProperty("skipToWorkflowStep")
  ) {
    to.query.skipToWorkflowStep = from.query.skipToWorkflowStep;
    queryUpdated = true;
  }

  if (queryUpdated) {
    next(to);
  } else {
    next();
  }
});

router.afterEach((to, from) => {
  console.log("router.afterEach", { to, from });

  let props = { from: from.name, to: to.name };

  if (document.referrer) {
    props.referrer = document.referrer;
  }
  if (to.query.hasOwnProperty("fbclid")) {
    props.fbclid = to.query.fbclid;
  }
  if (to.query.hasOwnProperty("a")) {
    analytics.setGlobalProperties({ sourceId: to.query.a });
  }
  if (to.query.hasOwnProperty("testing")) {
    analytics.setGlobalProperties({ testingMode: true });
  }
  if (to.query.hasOwnProperty("prefill")) {
    analytics.setGlobalProperties({ prefillMode: true });
  }

  analytics.track("View Page", props, { smartlook: true });
});

export default router;
